import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Delete, Download, Edit, FileText, User, Calendar, Settings,
} from 'react-feather';
import {
  Modal, Button, ConfigProvider, Popconfirm, message,
  Card, Form, Input, Select, Row, Col, Typography, Divider, Empty, Tag, Space, Tooltip,
} from 'antd';
import QRCode from 'qrcode';

import cierhaLogo from './CIERHA_Logo.png';
import ethosLogo from './Ethos_Logo.png';
import { DEFAULT_TEMPLATE, mergeTemplate } from './certificateTemplate';
import { applyMergeTemplate, buildMergeData } from './mergeFieldUtils';
import TemplateEditorModal from './TemplateEditorModal';
import './certificate.css';

const { Title, Text } = Typography;

const TEMPLATE_PATH = 'certificate-template/CIERHA/config';

const Certificate = ({ app }) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    genre: 'Homme',
    noms: '',
    domaine: '',
    specialite: '',
    lieuNaissance: '',
    dateNaissance: '',
    anneeAcademique: '',
    mention: 'Excellent',
    numeroRef: '',
    lieuEmission: 'Port-au-Prince',
    dateEmission: new Date().toISOString().split('T')[0],
  });
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [documents, setDocuments] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const db = getDatabase(app);

  useEffect(() => {
    onValue(ref(db, TEMPLATE_PATH), (snapshot) => {
      const data = snapshot.val();
      const merged = mergeTemplate(data);
      setTemplate(merged);
      const currentMention = form.getFieldValue('mention');
      if (!merged.mentionOptions.includes(currentMention)) {
        form.setFieldValue('mention', merged.mentionOptions[0]);
      }
    });
  }, [db, form]);

  useEffect(() => {
    onValue(ref(db, 'certificate/CIERHA/'), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setDocuments(
          Object.keys(data).map((key) => ({ id: key, ...data[key] }))
        );
      } else {
        setDocuments([]);
      }
    });
  }, [db]);

  useEffect(() => {
    const generateQRCode = async () => {
      if (!selectedDocument) return;
      const qrData = [
        'CERTIFICATE - CIERHA',
        `Name: ${selectedDocument.noms || ''}`,
        `Reference: ${selectedDocument.numeroRef || ''}`,
        `Domain: ${selectedDocument.domaine || ''}`,
        `Specialization: ${selectedDocument.specialite || ''}`,
        `Issue Date: ${selectedDocument.dateEmission || ''}`,
        'Status: Document Verified',
        `Issued by: ${template.universityName}`,
      ].join('\n');

      try {
        const qrCodeUrl = await QRCode.toDataURL(qrData, {
          width: 120,
          margin: 1,
          color: { dark: '#000000', light: '#FFFFFF' },
        });
        setQrCodeData(qrCodeUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };
    if (selectedDocument) generateQRCode();
  }, [selectedDocument, template.universityName]);

  const handleFormChange = (_, allValues) => {
    setFormData((prev) => ({ ...prev, ...allValues }));
  };

  const handleSubmit = (values) => {
    const requiredFields = [
      'noms', 'domaine', 'specialite', 'lieuNaissance',
      'dateNaissance', 'anneeAcademique', 'dateEmission',
    ];
    for (const field of requiredFields) {
      if (!values[field] || String(values[field]).trim() === '') {
        message.warning(`${field} est obligatoire!`);
        return;
      }
    }

    const documentRefPath = isEditing
      ? ref(db, `certificate/CIERHA/${editingId}`)
      : ref(db, `certificate/CIERHA/${values.noms.replace(/\s+/g, '_')}_${Date.now()}`);

    const dataToSave = isEditing
      ? { ...values }
      : { ...values, createdAt: new Date().toISOString() };

    set(documentRefPath, dataToSave)
      .then(() => {
        message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
        resetForm();
      })
      .catch((error) => {
        console.error('Error saving certificate:', error);
        message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
      });
  };

  const resetForm = () => {
    const defaults = {
      genre: 'Homme',
      noms: '',
      domaine: '',
      specialite: '',
      lieuNaissance: '',
      dateNaissance: '',
      anneeAcademique: '',
      mention: template.mentionOptions[0] || 'Excellent',
      numeroRef: '',
      lieuEmission: 'Port-au-Prince',
      dateEmission: new Date().toISOString().split('T')[0],
    };
    setFormData(defaults);
    form.setFieldsValue(defaults);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleEditDocument = (document) => {
    const values = {
      genre: document.genre || 'Homme',
      noms: document.noms || '',
      domaine: document.domaine || '',
      specialite: document.specialite || '',
      lieuNaissance: document.lieuNaissance || '',
      dateNaissance: document.dateNaissance || '',
      anneeAcademique: document.anneeAcademique || '',
      mention: document.mention || template.mentionOptions[0] || 'Excellent',
      numeroRef: document.numeroRef || '',
      lieuEmission: document.lieuEmission || 'Port-au-Prince',
      dateEmission: document.dateEmission || new Date().toISOString().split('T')[0],
    };
    setFormData(values);
    form.setFieldsValue(values);
    setIsEditing(true);
    setEditingId(document.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openTemplateEditor = () => {
    setTemplateModalVisible(true);
  };

  const handleSaveTemplate = (dataToSave) => {
    set(ref(db, TEMPLATE_PATH), {
      ...dataToSave,
      updatedAt: new Date().toISOString(),
    })
      .then(() => {
        message.success('Modèle de document enregistré');
        setTemplateModalVisible(false);
      })
      .catch((error) => {
        console.error('Error saving template:', error);
        message.error('Erreur lors de la sauvegarde du modèle');
      });
  };

  const showDocumentModal = (document) => {
    setSelectedDocument(document);
    setModalVisible(true);
  };

  const handleDeleteDocument = (id) => {
    remove(ref(db, `certificate/CIERHA/${id}`))
      .then(() => message.success('Document supprimé avec succès'))
      .catch((error) => {
        console.error('Error deleting document:', error);
        message.error('Erreur lors de la suppression');
      });
  };

  const downloadPDF = async () => {
    if (!selectedDocument) return;
    const element = document.getElementById('cierha-certificate-document');
    if (!element) return;

    try {
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const page = element.querySelector('.cierha-certificate-page');
      if (!page) return;

      page.style.backgroundColor = 'white';
      const images = page.querySelectorAll('img');
      await Promise.all(Array.from(images).map((img) => new Promise((resolve) => {
        if (img.complete) resolve();
        else { img.onload = resolve; img.onerror = resolve; }
      })));
      await new Promise((resolve) => setTimeout(resolve, 400));

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#FFFFFF',
        windowWidth: page.scrollWidth,
        windowHeight: page.scrollHeight,
      });

      const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', (pageWidth - w) / 2, 20, w, h);
      pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_CIERHA_Certificate.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Erreur lors de la génération du PDF');
    }
  };

  const mentionSelectOptions = (template.mentionOptions || []).map((m) => ({
    value: m,
    label: m,
  }));

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#045cb4',
          borderRadius: 8,
          fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        },
      }}
    >
      <div className="cierha-certificate-container">
        <div className="cierha-workspace">
        <div className="cierha-workspace-main">
        <Card className="cierha-form-card" bordered={false}>
          <div className="cierha-form-hero">
            <div className="cierha-form-logos">
              <img
                src={template.leftLogoUrl || cierhaLogo}
                alt="Logo CIERHA"
                className="cierha-form-logo-left"
              />
              <img
                src={template.rightLogoUrl || ethosLogo}
                alt="Logo Ethos"
                className="cierha-form-logo-right"
              />
            </div>
            <div className="cierha-form-hero-text">
              <Title level={3} className="cierha-form-title">Nouveau Certificat</Title>
            </div>
            <Button
              icon={<Settings size={16} />}
              size="large"
              onClick={openTemplateEditor}
              className="cierha-template-btn"
            >
              Modifier le document
            </Button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleFormChange}
            initialValues={formData}
            requiredMark="optional"
            className="cierha-ant-form"
          >
            <Divider orientation="left" className="cierha-section-divider">
              <Space><User size={16} /> Informations du candidat</Space>
            </Divider>

            <Row gutter={[20, 0]}>
              <Col xs={24} md={8}>
                <Form.Item label="Genre" name="genre" rules={[{ required: true }]}>
                  <Select
                    size="large"
                    options={[
                      { value: 'Homme', label: 'Homme (Mr.)' },
                      { value: 'Femme', label: 'Femme (Ms.)' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item label="Nom complet" name="noms" rules={[{ required: true }]}>
                  <Input size="large" placeholder="Ex: AMADOU MOUSSA DAOUDA" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[20, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Domaine" name="domaine" rules={[{ required: true }]}>
                  <Input size="large" placeholder="Ex: Finance" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Spécialisation" name="specialite" rules={[{ required: true }]}>
                  <Input size="large" placeholder="Ex: Business acquisition" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[20, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Lieu de naissance" name="lieuNaissance" rules={[{ required: true }]}>
                  <Input size="large" placeholder="Ex: Libreville" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Date de naissance" name="dateNaissance" rules={[{ required: true }]}>
                  <Input size="large" type="date" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[20, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Année académique" name="anneeAcademique" rules={[{ required: true }]}>
                  <Input size="large" placeholder="Ex: 2024-2025" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Mention" name="mention" rules={[{ required: true }]}>
                  <Select size="large" options={mentionSelectOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" className="cierha-section-divider">
              <Space><FileText size={16} /> Informations du document</Space>
            </Divider>

            <Row gutter={[20, 0]}>
              <Col xs={24} md={8}>
                <Form.Item label="N° de référence" name="numeroRef">
                  <Input size="large" placeholder="Ex: D204" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Lieu d'émission" name="lieuEmission">
                  <Input size="large" placeholder="Ex: Port-au-Prince" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Date d'émission" name="dateEmission" rules={[{ required: true }]}>
                  <Input size="large" type="date" />
                </Form.Item>
              </Col>
            </Row>

            <div className="cierha-form-actions">
              <Button type="primary" htmlType="submit" size="large" className="cierha-submit-btn">
                {isEditing ? 'Enregistrer les modifications' : 'Créer le certificat'}
              </Button>
              {isEditing && (
                <Button size="large" onClick={resetForm}>Annuler</Button>
              )}
            </div>
          </Form>
        </Card>
        </div>

        <aside className="cierha-workspace-sidebar">
        <Card
          className="cierha-list-card"
          bordered={false}
          title={(
            <Space>
              <Calendar size={18} />
              <span>Certificats enregistrés</span>
              <Tag color="blue">{documents.length}</Tag>
            </Space>
          )}
        >
          {documents.length === 0 ? (
            <Empty description="Aucun certificat créé pour le moment" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div className="cierha-doc-list">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="cierha-doc-row"
                  onClick={() => showDocumentModal(doc)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      showDocumentModal(doc);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="cierha-doc-row-info">
                    <Text strong className="cierha-doc-name">{doc.noms}</Text>
                    <Text type="secondary" className="cierha-doc-meta">
                      {doc.domaine} · {doc.specialite}
                    </Text>
                    {doc.numeroRef && (
                      <Text type="secondary" className="cierha-doc-ref">N° {doc.numeroRef}</Text>
                    )}
                  </div>
                  <div className="cierha-doc-row-actions" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Modifier">
                      <Button type="text" icon={<Edit size={16} />} onClick={() => handleEditDocument(doc)} />
                    </Tooltip>
                    <Popconfirm
                      title="Supprimer ce certificat ?"
                      onConfirm={() => handleDeleteDocument(doc.id)}
                      okText="Oui"
                      cancelText="Non"
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title="Supprimer">
                        <Button type="text" danger icon={<Delete size={16} />} />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        </aside>
        </div>

        <Modal
          title={<span style={{ color: 'white' }}>Aperçu - Certificat CIERHA</span>}
          open={modalVisible}
          onCancel={() => { setModalVisible(false); setSelectedDocument(null); }}
          width="95vw"
          style={{ top: 10 }}
          footer={[
            <Button
              key="download"
              type="primary"
              icon={<Download size={16} />}
              onClick={downloadPDF}
              style={{ backgroundColor: '#28a745', borderColor: '#28a745', fontWeight: 'bold' }}
            >
              Télécharger PDF
            </Button>,
            <Button key="close" onClick={() => { setModalVisible(false); setSelectedDocument(null); }}>
              Fermer
            </Button>,
          ]}
        >
          {selectedDocument && (
            <div className="cierha-certificate-modal-content">
              <CertificateDocument
                document={selectedDocument}
                template={template}
                qrCodeData={qrCodeData}
                defaultLogos={{ left: cierhaLogo, right: ethosLogo }}
              />
            </div>
          )}
        </Modal>

        <TemplateEditorModal
          open={templateModalVisible}
          onClose={() => setTemplateModalVisible(false)}
          onSave={handleSaveTemplate}
          template={template}
          defaultLogos={{ left: cierhaLogo, right: ethosLogo }}
        />
      </div>
    </ConfigProvider>
  );
};

const formatDateWithMonthName = (dateStr) => {
  const date = new Date(dateStr);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const CertificateDocument = ({ document: docData, template, qrCodeData, defaultLogos }) => {
  const leftLogo = template.leftLogoUrl || defaultLogos.left;
  const rightLogo = template.rightLogoUrl || defaultLogos.right;
  const mergeData = buildMergeData(docData, formatDateWithMonthName);
  const awardHtml = applyMergeTemplate(
    template.awardTemplate || DEFAULT_TEMPLATE.awardTemplate,
    mergeData,
  );
  const dateHtml = applyMergeTemplate(
    template.dateLineTemplate || DEFAULT_TEMPLATE.dateLineTemplate,
    mergeData,
  );

  return (
    <div id="cierha-certificate-document">
      <div className="cierha-certificate-page">
        <div className="cierha-certificate-header">
          <div className="cierha-header-logo-left">
            <img src={leftLogo} alt="Logo gauche" className="cierha-header-logo cierha-logo-queensland" />
          </div>
          <div className="cierha-header-center">
            <h1 className="cierha-university-name">{template.universityName}</h1>
            <h2 className="cierha-chair-name">{template.chairName}</h2>
            <h3 className="cierha-acronym">{template.acronym}</h3>
            <p className="cierha-address">{template.address}</p>
            <p className="cierha-contact">
              Web: <strong>{template.web}</strong>; Email: <strong>{template.email}</strong>
            </p>
          </div>
          <div className="cierha-header-logo-right">
            <img src={rightLogo} alt="Logo droit" className="cierha-header-logo cierha-logo-ethos" />
          </div>
        </div>

        <div className="cierha-separator" />

        <div className="cierha-title-row">
          <h1 className="cierha-main-title">{template.documentTitle}</h1>
          {docData.numeroRef && (
            <span className="cierha-ref-number">N° {docData.numeroRef}</span>
          )}
        </div>

        <div className="cierha-certificate-body">
          <p>{template.clause1}</p>
          <p>{template.clause2}</p>
          <p>{template.clause3}</p>
          <p
            className="cierha-award-paragraph"
            dangerouslySetInnerHTML={{ __html: awardHtml }}
          />
        </div>

        <p
          className="cierha-date-line"
          dangerouslySetInnerHTML={{ __html: dateHtml }}
        />

        <div className="cierha-signatures">
          <div className="cierha-signature-block">
            <p className="cierha-signature-name">{template.signatureLeftName}</p>
            <p className="cierha-signature-title">{template.signatureLeftTitle}</p>
          </div>
          <div className="cierha-signature-block cierha-signature-block-right">
            <p className="cierha-signature-name">{template.signatureRightName}</p>
            <p className="cierha-signature-title">{template.signatureRightTitle}</p>
          </div>
        </div>

        {qrCodeData && (
          <img src={qrCodeData} alt="QR Code" className="cierha-qr-code" />
        )}
      </div>
    </div>
  );
};

export default Certificate;
